
import React from 'react';
import { Link } from 'react-router-dom';
import { useCarouselPosts } from '@/hooks/blog/useCarouselPosts';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { LazyImage } from '@/components/ui/lazy-image';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

export function BlogCarousel() {
  const { posts, loading } = useCarouselPosts(4); // Increase to 4 posts for better variety
  const isMobile = useIsMobile();

  if (loading || posts.length === 0) {
    return null; // Don't render anything while loading or if no posts
  }

  return (
    <section className="py-8 bg-muted/30 sm:rounded-lg">
      <div className="container px-4 mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold">Latest Adventures</h2>
          <Link to="/blog">
            <Button variant="link" className="group">
              <span className="mr-1 text-base">View all</span>
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
        
        <Carousel className="w-full">
          <CarouselContent>
            {posts.map((post, index) => (
              <CarouselItem key={post.id} className={cn(
                isMobile ? "basis-full" : "md:basis-1/2 lg:basis-1/3"
              )}>
                <Link to={`/blog/${post.slug}`} className="block h-full">
                  <Card className="overflow-hidden h-full group border border-border/80">
                    <div className="aspect-[16/9] overflow-hidden relative">
                      <LazyImage
                        src={post.featured_image}
                        alt={post.title}
                        className={cn(
                          "object-cover w-full h-full",
                          "transition-transform duration-300 group-hover:scale-105"
                        )}
                        priority={index === 0} // Only prioritize first carousel item
                        blurUpPreview={true}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 p-5 text-white">
                        <h3 className="font-semibold text-lg sm:text-xl line-clamp-2">
                          {post.title}
                        </h3>
                        {post.excerpt && (
                          <p className="text-sm mt-2 line-clamp-2 text-white/90">
                            {post.excerpt}
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex" />
          <CarouselNext className="hidden md:flex" />
        </Carousel>
      </div>
    </section>
  );
}
